import React, { useMemo, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { Directions, Highlight, Search } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Divider, TextField, Tooltip } from "@mui/material";
import {
  ColDef,
  ColGroupDef,
  GetContextMenuItemsParams,
  GridOptions,
  MenuItemDef,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import mapboxgl from "mapbox-gl";

import { useGetMultiProjectQuery, useGetMultiProjectStatisticsQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { zoomTo } from "fond/map/redux";
import { highlightFeatures, selectFeature } from "fond/project/redux";
import * as turf from "fond/turf";
import { MultiProjectArea, MultiProjectAreaStatistic, SystemsOfMeasurement } from "fond/types";
import { GridId } from "fond/types/grids";
import { sqMetresToSqKilometers, sqMetresToSqMiles } from "fond/utils/area";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { convertToPercent, formatInteger, formatNumber, formatPercent } from "fond/utils/number";
import { AgGrid, BlockSpinner } from "fond/widgets";

import { convertDensity, convertPath, convertPathDensity, getImportantFiberProviders } from "../helper";

import { exportDataAsGeoJson } from "./exportGeoJson";

type Row = MultiProjectAreaStatistic & MultiProjectArea;

/**
 * To make sure the tooltip loads in the correct window.document
 * when the component is loaded within a floating window we
 * need to disable the use of a portal.
 */
const tooltipPopperProps = { disablePortal: true };

const gridOptions: GridOptions = {
  rowGroupPanelShow: "never",
  sideBar: false,
  pagination: false,
  rowSelection: {
    mode: "multiRow",
    checkboxes: false,
    headerCheckbox: false,
    enableClickSelection: true,
  },
  domLayout: "normal",
  getRowId: (params) => params.data.ID,
  // There is no column in this table where grouping makes sense
  defaultColDef: { enableRowGroup: false },
};

const numericFilterParams = {
  filterOptions: ["greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual", "inRange"],
};

const FeaturesTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const multiProjectId = useAppSelector((state) => state.project.projectId);
  const { data: multiProject, isLoading: isLoadingProject } = useGetMultiProjectQuery(multiProjectId);
  const { data: statistics, isLoading: isLoadingStatistics } = useGetMultiProjectStatisticsQuery(multiProjectId);
  const [selectedFeatures, setSelectedFeatures] = useState<Partial<mapboxgl.MapboxGeoJSONFeature>[]>([]);
  const gridRef = useRef<AgGridReact | null>(null);
  const { value: canExportGeoJSON } = useFeatureFlag("market_lens_subarea_export");
  const systemOfMeasurement = useMemo(() => multiProject?.SystemOfMeasurement ?? SystemsOfMeasurement.Metric, [multiProject?.SystemOfMeasurement]);

  const rowData: Row[] = useMemo(() => {
    if (!multiProject || !statistics) return [];
    return statistics.Areas.map((stats) => ({ ...stats, ...multiProject.Areas.find((a) => a.ID === stats.Area.ID) }) as Row);
  }, [multiProject, statistics]);

  // Only those providers included in the multi-project's assess configuration are shown in the features table.
  const importantProviders = useMemo(() => getImportantFiberProviders(multiProject), [multiProject]);

  const columns: Array<ColDef | ColGroupDef> = useMemo(() => {
    return [
      {
        field: "Name",
        headerName: "Name",
        flex: 1,
        minWidth: 125,
        filter: "agSetColumnFilter",
      },
      {
        field: "Score",
        headerName: "Score",
        width: 75,
        filter: "agNumberColumnFilter",
        filterParams: numericFilterParams,
        valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 0),
      },
      {
        field: "Boundary",
        headerName: `Area (${systemOfMeasurement === SystemsOfMeasurement.Imperial ? "sq mi" : "sq km"})`,
        width: 125,
        filter: "agNumberColumnFilter",
        filterParams: numericFilterParams,
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data.Boundary) return undefined;
          return systemOfMeasurement === "imperial"
            ? sqMetresToSqMiles(turf.area(params.data.Boundary))
            : sqMetresToSqKilometers(turf.area(params.data.Boundary));
        },
        valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 2),
      },
      {
        field: "StreetCounts.StreetIntersectionLengthMetres",
        headerName: `Paths (${systemOfMeasurement === SystemsOfMeasurement.Imperial ? "ft" : "m"})`,
        width: 125,
        filter: "agNumberColumnFilter",
        filterParams: numericFilterParams,
        valueGetter: (params: ValueGetterParams) => convertPath(params.data.StreetCounts.StreetIntersectionLengthMetres, systemOfMeasurement),
        valueFormatter: (params: ValueFormatterParams) => formatInteger(params.value),
      },

      {
        headerName: "Addresses",
        children: [
          {
            field: "AddressCounts.AddressCount",
            headerName: "Count",
            width: 100,
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 2),
          },
          {
            field: "AddressDensity.AddressesPerSquareMeters",
            headerName: systemOfMeasurement === SystemsOfMeasurement.Imperial ? "Addresses per square mile" : "Addresses per square kilometer",
            width: 125,
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueGetter: (params: ValueGetterParams) => convertDensity(params.data.AddressDensity.AddressesPerSquareMeters, systemOfMeasurement),
            valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 2),
          },
          {
            field: "AddressPathDensity.AddressesPerMeter",
            headerName: systemOfMeasurement === SystemsOfMeasurement.Imperial ? "Addresses per linear mile" : "Addresses per linear kilometer",
            width: 125,
            valueGetter: (params: ValueGetterParams) => convertPathDensity(params.data.AddressPathDensity.AddressesPerMeter, systemOfMeasurement),
            valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 4),
          },
          {
            headerName: "SDU (%)",
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueGetter: (params: ValueGetterParams<Row>) => convertToPercent(params.data?.AddressBreakdown.Sdu.Proportion),
            valueFormatter: (params: ValueFormatterParams) => formatPercent(params.value),
            width: 125,
          },
          {
            headerName: "Underserved (%)",
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueGetter: (params: ValueGetterParams<Row>) => convertToPercent(params.data?.AddressUnderservedProportion.AddressUnderservedProportion),
            valueFormatter: (params: ValueFormatterParams) => formatPercent(params.value),
            width: 125,
          },
          {
            field: "WeightedMedianIncome.WeightedMedianIncomeDollars",
            headerName: "Median Income ($)",
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 2),
            width: 145,
          },
        ],
      },
      {
        headerName: "Fiber coverage (%)",
        children: [
          {
            field: "FiberCoverageProportion.FiberCoverageProportion",
            headerName: "Overall",
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            valueGetter: (params: ValueGetterParams) => convertToPercent(params.data.FiberCoverageProportion.FiberCoverageProportion),
            valueFormatter: (params: ValueFormatterParams) => formatPercent(params.value ?? 0),
            width: 125,
          },
          ...importantProviders.map((provider) => ({
            headerName: provider,
            filter: "agNumberColumnFilter",
            filterParams: numericFilterParams,
            // use valueGetter instead of field to avoid dotted path issues
            valueGetter: (params: ValueGetterParams) => convertToPercent(params.data.ProviderFiberCoverage?.[provider]?.FiberCoverageProportion),
            valueFormatter: (params: ValueFormatterParams) => formatPercent(params.value ?? 0),
            width: 100,
          })),
        ],
      },
    ];
  }, [systemOfMeasurement, importantProviders]);

  if (!multiProject) return <BlockSpinner />;

  /**
   * Provides a custom set of context menu items (right click on row)
   */
  const getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    return [
      "copy",
      "copyWithHeaders",
      "separator",
      // There's no way to modify the existing 'export' item, but we can make a reasonable fake one.
      {
        name: "Export",
        icon: '<span class="ag-icon ag-icon-save"></span>',
        subMenu: [
          {
            name: "CSV Export",
            icon: '<span class="ag-icon ag-icon-csv"></span>',
            action: () => params.api.exportDataAsCsv(),
          },
          {
            name: "Excel Export",
            icon: '<span class="ag-icon ag-icon-excel"></span>',
            action: () => params.api.exportDataAsExcel(),
          },
          // GeoJSON export is only available if the feature flag is enabled
          ...(canExportGeoJSON
            ? [
                {
                  name: "GeoJSON Export",
                  icon: '<span class="ag-icon ag-icon-pivot"></span>',
                  action: () => exportDataAsGeoJson(params.api, true, (row) => row.Boundary),
                },
              ]
            : []),
        ],
      },
      "separator",
      {
        // custom item
        name: "Zoom to Feature",
        action: () => {
          if (params.node) {
            const { Boundary, ID } = params.node.data;
            const mapboxFeature: Partial<mapboxgl.MapboxGeoJSONFeature> = {
              ...turf.feature(Boundary, { boundaryId: ID }, { id: ID }),
              source: "multiProject-source",
            };
            // Zoom to feature or the group of features
            dispatch(zoomTo([mapboxFeature]));
          }
        },
        icon: renderToString(<Directions style={{ verticalAlign: "middle", fontSize: "18px" }} />),
      },
    ];
  };

  /**
   * Callback function called when a row is double clicked
   * Feature is zoomed to & selected
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    dispatch(zoomTo(selectedFeatures));
    dispatch(selectFeature(selectedFeatures[0]));
    dispatch(
      highlightFeatures({
        projectId: multiProjectId,
        features: selectedFeatures,
      })
    );
  };

  /**
   * Callback function called when row selection changes within the grid.
   * Enable zoom if at least one feature has geometry.
   */
  const onSelectionChanged = (event: SelectionChangedEvent<Row>) => {
    const features: Partial<mapboxgl.MapboxGeoJSONFeature>[] = [];
    const selectedNodes = event.api.getSelectedNodes();
    selectedNodes.forEach(({ data }) => {
      if (data?.Boundary) {
        const mapboxFeature: Partial<mapboxgl.MapboxGeoJSONFeature> = {
          ...turf.feature(data.Boundary, { boundaryId: data.ID }, { id: data.ID }),
          source: "multiProject-source",
        };
        features.push(mapboxFeature);
      }
    });
    setSelectedFeatures(features);
  };

  const handleHighlight = () => {
    dispatch(
      highlightFeatures({
        projectId: multiProjectId,
        features: selectedFeatures,
      })
    );
  };

  /**
   * Callback function for zooming to selected features within the table
   */
  const handleZoomTo = () => {
    dispatch(zoomTo(selectedFeatures));
  };

  /**
   * Callback function that is passed the filter text as it changes
   */
  const handleOnFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    gridRef.current?.api?.setGridOption("quickFilterText", event.target.value ?? "");
  };

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" flexDirection="column">
      <Box display="flex" flexDirection="row" alignItems="center" m={0.5}>
        <TextField
          variant="outlined"
          data-testid="ag-grid-external-input"
          margin="dense"
          name="search"
          onChange={handleOnFilterChange}
          placeholder="Search..."
          inputProps={{ sx: { p: 1, fontSize: 12 } }}
          InputProps={{
            startAdornment: <Search />,
          }}
          sx={{ m: 0 }}
        />
        <Box height="100%" mx={1}>
          <Divider orientation="vertical" />
        </Box>
        <ButtonGroup variant="outlined">
          <Tooltip title="Zoom to subarea" PopperProps={tooltipPopperProps}>
            <span>
              <Button
                variant="text"
                size="small"
                disabled={!selectedFeatures.length}
                onClick={handleZoomTo}
                startIcon={<Directions color={selectedFeatures.length ? "primary" : "disabled"} />}
              >
                Zoom
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Highlight Subareas" PopperProps={tooltipPopperProps}>
            <span>
              <Button
                variant="text"
                size="small"
                disabled={!selectedFeatures.length}
                onClick={handleHighlight}
                startIcon={<Highlight color={selectedFeatures.length ? "primary" : "disabled"} />}
              >
                Highlight
              </Button>
            </span>
          </Tooltip>
        </ButtonGroup>
      </Box>

      <Box flexGrow={1}>
        <AgGrid
          id={GridId.CITY_PLANNER_FEATURES_TABLE}
          ref={gridRef}
          gridOptions={gridOptions}
          getContextMenuItems={getContextMenuItems}
          variant="borderless"
          size="compact"
          columnDefs={columns}
          rowData={rowData}
          loading={isLoadingProject || isLoadingStatistics}
          onSelectionChanged={onSelectionChanged}
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </Box>
    </Box>
  );
};

export default FeaturesTable;
